import React, { useState } from 'react';
import {
  Card,
  Button,
  Alert,
  Collapse,
  Descriptions,
  Tag,
  Space,
  Spin,
  List,
  Typography,
  message,
  Result,
  Empty,
} from 'antd';
import {
  RobotOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
} from '@ant-design/icons';
import { proposalApi } from '../../services/proposal';
import type { ProductInsight } from '../../types/proposal';
import { COLORS } from '../../constants/colors';

const { Text, Paragraph } = Typography;

// 字段名中文映射表
const FIELD_LABEL_MAP: Record<string, string> = {
  // 一级面板
  product_analysis: '产品定位分析',
  target_audience: '目标用户画像',
  selling_points: '核心卖点提炼',
  voc_analysis: '用户评价分析（VOC）',
  influencer_marketing_status: '红人营销现状',
  competitor_analysis: '竞品产品分析',
  competitor_influencer_strategy: '竞品红人策略',
  // 目标用户子字段
  demographics: '人口特征',
  interests: '兴趣爱好',
  behaviors: '行为特征',
  pain_points: '痛点需求',
  // VOC 子字段
  positive_reviews: '正面评价',
  negative_reviews: '负面评价',
  key_themes: '关键主题',
  summary: '总结',
  // 产品分析子字段
  positioning: '产品定位',
  category: '产品品类',
  market_position: '市场定位',
  unique_value: '独特价值',
  price_range: '价格区间',
  price_positioning: '价格定位',
  // 卖点子字段
  core_selling_points: '核心卖点',
  emotional_appeal: '情感诉求',
  functional_benefits: '功能优势',
  differentiation: '差异化优势',
  // 竞品分析子字段
  competitors: '竞品列表',
  comparison: '对比分析',
  advantages: '我方优势',
  disadvantages: '我方劣势',
  opportunities: '市场机会',
  // 红人营销子字段
  current_status: '当前状态',
  platforms: '平台分布',
  content_types: '内容类型',
  budget: '预算',
  strategy: '营销策略',
  recommendations: '建议',
  // 平台策略子字段
  primary: '主要平台',
  secondary: '次要平台',
  supplementary: '补充平台',
  // 通用字段
  name: '名称',
  description: '描述',
  type: '类型',
  platform: '平台',
  content_type: '内容类型',
  follower_range: '粉丝范围',
  engagement_rate: '互动率',
  estimated_cost: '预估费用',
  notes: '备注',
  reason: '原因',
  impact: '影响',
  suggestion: '建议',
  priority: '优先级',
  status: '状态',
  trend: '趋势',
  score: '评分',
  percentage: '占比',
  count: '数量',
  total: '合计',
  average: '平均值',
  min: '最小值',
  max: '最大值',
  source: '来源',
  url: '链接',
  title: '标题',
  content: '内容',
  date: '日期',
  period: '周期',
  region: '地区',
  country: '国家',
  language: '语言',
  age: '年龄',
  gender: '性别',
  income: '收入',
  education: '学历',
  occupation: '职业',
  lifestyle: '生活方式',
  values: '价值观',
  motivation: '动机',
  challenges: '挑战',
  goals: '目标',
  preferences: '偏好',
  channels: '渠道',
  frequency: '频率',
  duration: '时长',
  format: '格式',
  tone: '调性',
  style: '风格',
  theme: '主题',
  topic: '话题',
  hashtag: '话题标签',
  mention: '提及',
  sentiment: '情感倾向',
  rating: '评分',
  review: '评价',
  feedback: '反馈',
  comment: '评论',
  opinion: '观点',
  insight: '洞察',
  finding: '发现',
  conclusion: '结论',
  action_item: '行动项',
  next_step: '下一步',
  timeline: '时间线',
  milestone: '里程碑',
  kpi: 'KPI指标',
  metric: '指标',
  benchmark: '基准',
  target: '目标值',
  actual: '实际值',
  gap: '差距',
  roi: '投资回报率',
  conversion_rate: '转化率',
  click_through_rate: '点击率',
  awareness: '品牌认知',
  reach: '触达',
  impression: '曝光',
  engagement: '互动',
  follower_growth: '粉丝增长',

  // ===== 营销策略相关 =====
  content_strategy: '内容策略',
  platform_strategy: '平台策略',
  marketing_strategy: '营销策略',
  influencer_strategy: '红人策略',
  collaboration_strategy: '合作策略',
  pricing_strategy: '定价策略',
  distribution_strategy: '分发策略',
  growth_strategy: '增长策略',

  // ===== 用户画像相关 =====
  consumption_habits: '消费习惯',
  purchase_behavior: '购买行为',
  shopping_preferences: '购物偏好',
  media_consumption: '媒体消费',
  online_behavior: '线上行为',
  social_media_usage: '社交媒体使用',
  content_preferences: '内容偏好',
  brand_preferences: '品牌偏好',
  decision_factors: '决策因素',
  buying_motivation: '购买动机',
  price_sensitivity: '价格敏感度',

  // ===== 营销效果指标 =====
  estimated_roi: '预估投资回报率',
  expected_roi: '预期投资回报率',
  estimated_reach: '预估触达量',
  estimated_engagement: '预估互动量',
  estimated_conversion: '预估转化量',
  estimated_impressions: '预估曝光量',
  expected_reach: '预期触达量',
  expected_conversions: '预期转化量',
  estimated_engagement_rate: '预估互动率',
  based_on_analysis: '基于分析',
  regions: '地区',
  age_range: '年龄范围',
  cost_per_engagement: '单次互动成本',
  cost_per_click: '单次点击成本',
  cost_per_conversion: '单次转化成本',
  cost_effectiveness: '成本效益',
  brand_awareness: '品牌知名度',
  brand_sentiment: '品牌情感',
  audience_growth: '受众增长',
  view_count: '观看量',
  like_count: '点赞量',
  comment_count: '评论量',
  share_count: '分享量',
  save_count: '收藏量',

  // ===== 红人相关 =====
  influencer_type: '红人类型',
  influencer_tier: '红人层级',
  influencer_count: '红人数量',
  collaboration_type: '合作类型',
  collaboration_format: '合作形式',
  content_format: '内容形式',
  posting_frequency: '发布频率',
  audience_overlap: '受众重叠度',
  audience_demographics: '受众人口特征',
  audience_interests: '受众兴趣',
  authenticity_score: '真实度评分',
  influence_score: '影响力评分',
  niche: '细分领域',
  specialization: '专业方向',

  // ===== 竞品分析相关 =====
  competitor_name: '竞品名称',
  competitor_strategy: '竞品策略',
  market_share: '市场份额',
  competitive_advantage: '竞争优势',
  competitive_landscape: '竞争格局',
  market_positioning: '市场定位',
  brand_positioning: '品牌定位',
  value_proposition: '价值主张',
  unique_selling_point: '独特卖点',
  key_differentiator: '关键差异化',
  strengths: '优势',
  weaknesses: '劣势',
  threats: '威胁',
  swot_analysis: 'SWOT分析',

  // ===== 产品相关 =====
  product_features: '产品特点',
  product_benefits: '产品优势',
  product_positioning: '产品定位',
  product_category: '产品品类',
  product_line: '产品线',
  target_market: '目标市场',
  market_size: '市场规模',
  market_growth: '市场增长',
  market_trend: '市场趋势',
  industry_trend: '行业趋势',
  consumer_trend: '消费趋势',

  // ===== 内容相关 =====
  content_theme: '内容主题',
  content_angle: '内容角度',
  content_hook: '内容亮点',
  content_calendar: '内容日历',
  key_message: '核心信息',
  call_to_action: '行动号召',
  creative_direction: '创意方向',
  visual_style: '视觉风格',
  brand_voice: '品牌调性',
  messaging: '信息传达',

  // ===== 时间与周期 =====
  campaign_duration: '活动周期',
  launch_date: '启动日期',
  end_date: '结束日期',
  peak_period: '高峰期',
  off_peak_period: '淡季',
  seasonal_factor: '季节因素',

  // ===== 预算相关 =====
  total_budget: '总预算',
  budget_allocation: '预算分配',
  budget_breakdown: '预算明细',
  actual_cost: '实际费用',
  cost_saving: '成本节省',

  // ===== 其他通用 =====
  overview: '概述',
  analysis: '分析',
  findings: '发现',
  key_findings: '关键发现',
  key_insights: '关键洞察',
  key_takeaways: '核心要点',
  action_items: '行动项',
  next_steps: '下一步计划',
  risk_factors: '风险因素',
  success_factors: '成功因素',
  best_practices: '最佳实践',
  case_studies: '案例分析',
  reference: '参考',
  appendix: '附录',
};

// 获取字段中文标签的辅助函数
const getFieldLabel = (key: string): string => {
  if (FIELD_LABEL_MAP[key]) return FIELD_LABEL_MAP[key];
  // fallback: 将下划线转空格并首字母大写（保底方案）
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// 面板标题配置
const panelConfig = [
  { key: 'product_analysis', title: '产品定位分析', order: 1 },
  { key: 'target_audience', title: '目标用户画像', order: 2 },
  { key: 'selling_points', title: '核心卖点提炼', order: 3 },
  { key: 'voc_analysis', title: '用户评价分析（VOC）', order: 4 },
  { key: 'influencer_marketing_status', title: '红人营销现状', order: 5 },
  { key: 'competitor_analysis', title: '竞品产品分析', order: 6 },
  { key: 'competitor_influencer_strategy', title: '竞品红人策略', order: 7 },
];

// 渲染对象数据
const renderObjectData = (data: Record<string, unknown> | undefined) => {
  if (!data || Object.keys(data).length === 0) {
    return <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <Descriptions column={1} size="small" bordered>
      {Object.entries(data).map(([key, value]) => {
        const label = getFieldLabel(key);
        return (
          <Descriptions.Item key={key} label={label}>
            {renderValue(value)}
          </Descriptions.Item>
        );
      })}
    </Descriptions>
  );
};

// 将任意值规范化为数组
const normalizeToArray = (data: unknown): unknown[] => {
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    const parts = data.split(/[、，,]/).map((s) => s.trim()).filter(Boolean);
    return parts.length > 0 ? parts : [data];
  }
  return [data];
};

// 渲染数组数据
const renderArrayData = (data: unknown) => {
  if (!data) {
    return <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  // 非数组类型：对象走 renderObjectData，其他包装为数组
  if (!Array.isArray(data)) {
    if (typeof data === 'object' && data !== null) {
      return renderObjectData(data as Record<string, unknown>);
    }
    return renderArrayData(normalizeToArray(data));
  }

  if (data.length === 0) {
    return <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <List
      size="small"
      dataSource={data}
      renderItem={(item, index) => (
        <List.Item key={index}>
          {typeof item === 'object' && item !== null ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {Object.entries(item as Record<string, unknown>).map(([key, value]) => (
                <div key={key}>
                  <Text strong>{getFieldLabel(key)}: </Text>
                  <Text>{renderValue(value)}</Text>
                </div>
              ))}
            </Space>
          ) : (
            <Text>{String(item)}</Text>
          )}
        </List.Item>
      )}
    />
  );
};

// 渲染标签列表（兼容数组、字符串、其他类型）
const renderTagList = (data: unknown) => {
  if (data === null || data === undefined) {
    return <Text type="secondary">暂无数据</Text>;
  }

  let items: unknown[];
  if (Array.isArray(data)) {
    items = data;
  } else if (typeof data === 'string') {
    items = data.split(/[、，,]/).map((s) => s.trim()).filter(Boolean);
  } else {
    items = [data];
  }

  if (items.length === 0) {
    return <Text type="secondary">暂无数据</Text>;
  }

  return (
    <Space wrap>
      {items.map((item, index) => (
        <Tag key={index} color="blue">
          {typeof item === 'object' ? Object.values(item as Record<string, unknown>).join(' · ') : String(item)}
        </Tag>
      ))}
    </Space>
  );
};

// 智能渲染任意值的函数（递归处理嵌套结构）
const renderValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) {
    return <Text type="secondary">-</Text>;
  }

  // 字符串
  if (typeof value === 'string') {
    return <Text>{value}</Text>;
  }

  // 数字
  if (typeof value === 'number') {
    return <Text>{value}</Text>;
  }

  // 布尔值
  if (typeof value === 'boolean') {
    return <Text>{value ? '是' : '否'}</Text>;
  }

  // 数组
  if (Array.isArray(value)) {
    if (value.length === 0) return <Text type="secondary">-</Text>;

    // 简单值数组（字符串/数字）→ Tag 标签组
    if (value.every((item) => typeof item === 'string' || typeof item === 'number')) {
      return (
        <Space wrap>
          {value.map((item, index) => (
            <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
              {String(item)}
            </Tag>
          ))}
        </Space>
      );
    }

    // 对象数组 → 列表
    return (
      <div style={{ paddingLeft: 16 }}>
        {value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            {typeof item === 'object' && item !== null ? (
              <div style={{ padding: '8px 12px', background: 'rgba(0,0,0,0.02)', borderRadius: 4 }}>
                {Object.entries(item as Record<string, unknown>).map(([k, v]) => (
                  <div key={k} style={{ marginBottom: 4 }}>
                    <Text strong style={{ marginRight: 8 }}>{getFieldLabel(k)}：</Text>
                    {renderValue(v)}
                  </div>
                ))}
              </div>
            ) : (
              <Text>{String(item)}</Text>
            )}
          </div>
        ))}
      </div>
    );
  }

  // 普通对象 → key: value 分组列表
  if (typeof value === 'object') {
    return (
      <div style={{ paddingLeft: 16 }}>
        {Object.entries(value as Record<string, unknown>).map(([k, v]) => (
          <div key={k} style={{ marginBottom: 4 }}>
            <Text strong style={{ marginRight: 8 }}>{getFieldLabel(k)}：</Text>
            {renderValue(v)}
          </div>
        ))}
      </div>
    );
  }

  return <Text>{String(value)}</Text>;
};

// 渲染面板内容
const renderPanelContent = (key: string, data: unknown) => {
  if (!data) {
    return <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  try {
  // 根据不同面板类型渲染不同内容
  switch (key) {
    case 'target_audience':
      // 目标用户画像 - 用Tag列表展示
      if (Array.isArray(data)) {
        return renderTagList(data);
      }
      if (typeof data === 'object') {
        const audience = data as Record<string, unknown>;
        // 根据子字段实际类型选择合适的渲染方式
        const renderAudienceField = (value: unknown): React.ReactElement | React.ReactNode => {
          if (Array.isArray(value)) return renderTagList(value);
          if (typeof value === 'string') return renderTagList(value);
          if (typeof value === 'object' && value !== null) return renderObjectData(value as Record<string, unknown>);
          return renderValue(value);
        };
        return (
          <div>
            {!!audience.demographics && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>人口特征：</Text>
                {renderAudienceField(audience.demographics)}
              </div>
            )}
            {!!audience.interests && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>兴趣爱好：</Text>
                {renderAudienceField(audience.interests)}
              </div>
            )}
            {!!audience.behaviors && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>行为特征：</Text>
                {renderAudienceField(audience.behaviors)}
              </div>
            )}
            {!!audience.pain_points && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>痛点需求：</Text>
                {renderAudienceField(audience.pain_points)}
              </div>
            )}
            {/* 其他字段 */}
            {Object.entries(audience)
              .filter(([k]) => !['demographics', 'interests', 'behaviors', 'pain_points'].includes(k))
              .map(([k, v]) => (
                <div key={k} style={{ marginBottom: 8 }}>
                  <Text strong>{getFieldLabel(k)}：</Text>
                  {renderValue(v)}
                </div>
              ))}
          </div>
        );
      }
      return <Paragraph>{String(data)}</Paragraph>;

    case 'selling_points':
      // 核心卖点 - 用List展示
      if (Array.isArray(data)) {
        return (
          <List
            size="small"
            dataSource={data}
            renderItem={(item, index) => (
              <List.Item>
                <Space align="start">
                  <Tag color="green">{index + 1}</Tag>
                  <div>
                    {typeof item === 'object' && item !== null ? (
                      <>
                        {(item as Record<string, unknown>).point && (
                          <Text strong>{String((item as Record<string, unknown>).point)}</Text>
                        )}
                        {(item as Record<string, unknown>).description && (
                          <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4 }}>
                            {String((item as Record<string, unknown>).description)}
                          </Paragraph>
                        )}
                        {!(item as Record<string, unknown>).point && (
                          <Text>{JSON.stringify(item)}</Text>
                        )}
                      </>
                    ) : (
                      <Text>{String(item)}</Text>
                    )}
                  </div>
                </Space>
              </List.Item>
            )}
          />
        );
      }
      return <Paragraph>{String(data)}</Paragraph>;

    case 'voc_analysis':
      // VOC分析
      if (typeof data === 'object' && data !== null) {
        const voc = data as Record<string, unknown>;
        return (
          <div>
            {!!voc.positive_reviews && (
              <Card title="正面评价" size="small" style={{ marginBottom: 16 }}>
                {renderArrayData(voc.positive_reviews as unknown[])}
              </Card>
            )}
            {!!voc.negative_reviews && (
              <Card title="负面评价" size="small" style={{ marginBottom: 16 }}>
                {renderArrayData(voc.negative_reviews as unknown[])}
              </Card>
            )}
            {!!voc.key_themes && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>关键主题：</Text>
                {renderTagList(voc.key_themes as unknown[])}
              </div>
            )}
            {!!voc.summary && (
              <div>
                <Text strong>总结：</Text>
                <Paragraph>{String(voc.summary)}</Paragraph>
              </div>
            )}
            {/* 其他字段 */}
            {Object.entries(voc)
              .filter(([k]) => !['positive_reviews', 'negative_reviews', 'key_themes', 'summary'].includes(k))
              .map(([k, v]) => (
                <div key={k} style={{ marginBottom: 8 }}>
                  <Text strong>{getFieldLabel(k)}：</Text>
                  {renderValue(v)}
                </div>
              ))}
          </div>
        );
      }
      return <Paragraph>{String(data)}</Paragraph>;

    case 'product_analysis':
    case 'influencer_marketing_status':
    case 'competitor_analysis':
    case 'competitor_influencer_strategy':
      // 先检查数组（数组也是object，必须优先判断）
      if (Array.isArray(data)) {
        return renderArrayData(data);
      }
      if (typeof data === 'object' && data !== null) {
        return renderObjectData(data as Record<string, unknown>);
      }
      return <Paragraph>{String(data)}</Paragraph>;

    default:
      if (Array.isArray(data)) {
        return renderArrayData(data);
      }
      if (typeof data === 'object' && data !== null) {
        return renderObjectData(data as Record<string, unknown>);
      }
      return <Paragraph>{String(data)}</Paragraph>;
  }
  } catch (error) {
    console.error(`[InsightTab] 渲染面板 "${key}" 失败:`, error);
    return (
      <Alert
        type="warning"
        message="数据格式异常"
        description="该面板数据格式与预期不符，无法正常渲染。请尝试重新生成洞察报告。"
        showIcon
      />
    );
  }
};

interface InsightTabProps {
  proposalId: string;
  insight?: ProductInsight | null;
  requirementStatus?: string;
  onRefresh: () => void;
}

export const InsightTab: React.FC<InsightTabProps> = ({
  proposalId,
  insight,
  requirementStatus,
  onRefresh,
}) => {
  const [loading, setLoading] = useState(false);

  // 生成/重新生成洞察报告
  const handleGenerate = async () => {
    setLoading(true);
    try {
      await proposalApi.generateProductInsight(proposalId);
      message.success('AI产品洞察已启动，请等待生成完成');
      onRefresh(); // 让父组件负责数据刷新和轮询
    } catch (error: unknown) {
      message.error('启动产品洞察失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 重新生成
  const handleRegenerate = () => {
    handleGenerate();
  };

  // 前置检查
  if (requirementStatus !== 'completed' && requirementStatus !== 'confirmed') {
    return (
      <Card>
        <Alert
          type="warning"
          message="请先确认需求解构结果"
          description="在生成产品洞察报告之前，需要先完成需求解构并确认结果。"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Empty description="请先完成需求解构" />
      </Card>
    );
  }

  // 未生成状态
  if (!insight || insight.status === 'pending') {
    return (
      <Card>
        <Empty
          description={
            <span>
              暂无产品洞察报告
              <br />
              <Text type="secondary">点击下方按钮生成AI产品洞察报告</Text>
            </span>
          }
        >
          <Space direction="vertical" align="center">
            <Button
              type="primary"
              icon={<RobotOutlined />}
              onClick={handleGenerate}
              loading={loading}
              size="large"
            >
              {loading ? 'AI正在生成中...' : '生成洞察报告'}
            </Button>
            <Text type="secondary">
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              预计需要 1-2 分钟
            </Text>
          </Space>
        </Empty>
      </Card>
    );
  }

  // 处理中状态
  if (insight.status === 'processing' || loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 24 }}>
            <Text type="secondary" style={{ fontSize: 16 }}>
              AI正在分析产品并生成洞察报告，请稍候...
            </Text>
          </div>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">预计需要 1-2 分钟</Text>
          </div>
        </div>
      </Card>
    );
  }

  // 失败状态
  if (insight.status === 'failed') {
    return (
      <Card>
        <Result
          status="error"
          title="产品洞察生成失败"
          subTitle={insight.error_message || '生成过程中发生错误，请重试'}
          extra={
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleRegenerate}
            >
              重新生成
            </Button>
          }
        />
      </Card>
    );
  }

  // 已生成状态
  const insightResult: Record<string, unknown> = {
    product_analysis: insight.product_analysis,
    target_audience: insight.target_audience,
    selling_points: insight.selling_points,
    voc_analysis: insight.voc_analysis,
    influencer_marketing_status: insight.influencer_marketing_status,
    competitor_analysis: insight.competitor_analysis,
    competitor_influencer_strategy: insight.competitor_influencer_strategy,
  };

  // 构建折叠面板项
  const collapseItems = panelConfig.map((panel) => {
    const data = insightResult?.[panel.key];
    return {
      key: String(panel.order),
      label: (
        <Space>
          <Tag color="blue">{panel.order}</Tag>
          <Text strong>{panel.title}</Text>
        </Space>
      ),
      children: (
        <div>
          {renderPanelContent(panel.key, data)}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px dashed ${COLORS.gray200}` }}>
            <Space>
              <DatabaseOutlined style={{ color: COLORS.textSecondary }} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                数据来源：AI分析和知识库
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                · 生成时间：{insight.updated_at
                  ? new Date(insight.updated_at).toLocaleString()
                  : new Date(insight.created_at).toLocaleString()}
              </Text>
            </Space>
          </div>
        </div>
      ),
    };
  });

  return (
    <Card>
      <Collapse
        defaultActiveKey={['1', '2', '3']}
        items={collapseItems}
        style={{ marginBottom: 24 }}
      />

      {/* 底部操作栏 */}
      <div style={{ borderTop: `1px solid ${COLORS.gray200}`, paddingTop: 16 }}>
        <Space>
          <Button icon={<SyncOutlined />} onClick={handleRegenerate}>
            重新生成
          </Button>
        </Space>
        {insight.tokens_used && (
          <Text type="secondary" style={{ float: 'right' }}>
            消耗Token数：{insight.tokens_used}
          </Text>
        )}
      </div>
    </Card>
  );
};
